import React from 'react'
import {
  AdditionalInfoRequirementType,
  RequirementReasonCode,
  type AdditionalInfoRequirement,
  type SolarDoc,
  type NameMismatchDetails
} from '@oneethos/shared'
import { MultiFileUpload } from '../../components/forms/multi-file-upload'
import NameMismatchDetailsForm from './NameMismatchDetailsForm'


const getGovernmentIdDescription = (reasonCodes: RequirementReasonCode[]): React.ReactNode => {
  const hasNameMismatch = reasonCodes.includes(RequirementReasonCode.NameMismatch)
  const hasAddress = reasonCodes.includes(RequirementReasonCode.AddressVerification)

  if (hasNameMismatch && hasAddress) {
    return (<>
      <p>The name on your credit report differs from your application, and your address could not be verified.</p>
      <p>Please upload a government-issued photo ID.</p>
    </>
    )
  }
  if (hasNameMismatch) {
    return (<>
      <p>The name on your credit report differs from your application.</p>
      <p>Please upload a government-issued photo ID (e.g. driver's license or passport).</p>
    </>)
  }
  return (<>
    <p>Your address could not be verified.</p>
    <p>Please upload a government-issued photo ID.</p>
  </>)
}

const NameMismatchReviewDescription = () => (
  <p>The name on your credit report differs from your application.</p>
)

const AditionalRequirementForm = ({
  requirement,
  borrowerKey,
  onDocumentsChange,
  onNameMismatchChange
}: {
  requirement: AdditionalInfoRequirement
  borrowerKey: string
  onDocumentsChange: (files: File[]) => void
  onNameMismatchChange: (updated: NameMismatchDetails) => void
}) => {
  const isGovId = requirement.type === AdditionalInfoRequirementType.GovernmentPhotoId
  const isNameMismatchReview = requirement.type === AdditionalInfoRequirementType.NameMismatchReview
  const nameMistmatchCode = requirement.reasonCodes?.includes(RequirementReasonCode.NameMismatch)
  const reasonCodes = requirement.reasonCodes ?? []

  let title = 'Mortgage documentation'
  let description: React.ReactNode =
    <p>Upload a copy of your mortgage statement or other mortgage documentation.</p>
  if (isGovId) {
    title = 'Government-issued photo ID'
    description = getGovernmentIdDescription(reasonCodes)
  } else if (isNameMismatchReview) {
    title = 'Name difference'
    description = <NameMismatchReviewDescription />
  }

  const nameMismatchDetails: NameMismatchDetails =
    requirement.reasonDetails?.nameMismatchDetails ?? {
      hadLifeEvent: null,
      lifeEventType: null,
      supportingDocument: null
    }

  const hasGovIdFile = (requirement.documents?.length ?? 0) > 0
  const showNameMismatchForm =
    nameMistmatchCode &&
    (isNameMismatchReview || hasGovIdFile)
  const showRequirementUpload = !isNameMismatchReview

  return (
    <div className="form-group mb-4">
      <h4>{title}</h4>
      {description}
      {showRequirementUpload ? (
        <MultiFileUpload
          docs={(requirement.documents ?? []) as (File & SolarDoc)[]}
          onChange={onDocumentsChange}
        />
      ) : null}
      {showNameMismatchForm && (
        <NameMismatchDetailsForm
          nameMismatchDetails={nameMismatchDetails}
          borrowerKey={borrowerKey}
          onChange={onNameMismatchChange}
        />
      )}
    </div>
  )
}

export default AditionalRequirementForm
